import { defineStore } from 'pinia';
import api from '@/js/api.js';
import app from '@/firebase';
import axios from 'axios';
import { getDatabase, ref, onValue } from 'firebase/database';
import { useDonorStore } from './DonorStore';
import { useModesStore } from './ModesStore.js';
import { useCompanyAdminStore } from './CompanyAdminStore.js';
import router from '@/router/index.js';

// Define the store
export const useFundraiserStore = defineStore('fundraiserStore', {
  state: () => ({
    organizations: [],
    fundraiserDonationData: [],
    fundraiserDonations: [],
    isLoading: false,
    createFundraiser: false,
    editFundraiser: false,
    currentStep: 1,
    fundraiserTitle: '',
    urlLink: '',
    goal: null,
    image: '',
    story: '',
    heroLink: '',
    errors: {},
    domain: '',
    fundraisers_id: null,
    success: false,
    saveError: 'An error occurred.',
    fundraiserUpdated: false,
    fundraiserCreationAllowed: false,
    newlyCreatedFundraiserId: '',
    benefittingOrgHostsId: null,
    benefittingOrgName: null,
    benefittingCompanyGivingId: null,
    companyGiving: null,
    processingReceipt: false,
    errorDisplay: false,
    sent: false,
    axiosRequest: null,
    creatorName: '',
  }),

  actions: {
    setLoading(status) {
      this.isLoading = status;
    },

    async getSingleReceipt(transactionsId) {
      const donorStore = useDonorStore();
      const url = `/fundraiser-donations?hosts_id=${donorStore.donorInfo.hosts_id}&donorAccounts_id=${donorStore.donorInfo.donorAccounts_id}&transactions_id=${transactionsId}`;
      try {
        this.processingReceipt = true;
        const response = await api.get(url, { mode: 'cors' });
        this.axiosRequest = null;
        const data = response.data;
        this.processingReceipt = false;
        this.errorDisplay = false;
        this.sent = true;
      } catch (error) {
        this.processingReceipt = false;
        this.errorDisplay = true;
        this.sent = false;
      }
    },
    async deactivateFundraiser(donorId, fundraiserId) {
      this.setLoading(true); // Start loading
      try {
        const response = await api.post(
          `/toggle-fundraiser-status?donorAccounts_id=${donorId}&fundraisers_id=${fundraiserId}&action=unpublish`,
        );
      } catch (error) {
        console.error('Failed to deactivate account:', error);
      } finally {
        this.setLoading(false); // Stop loading regardless of the outcome
      }
    },
    async fetchFundraisers() {
      const response = await api.get(`/retrieve-fundraisers`);
      this.fundraisers = response.data.data;
    },
    async fetchFundraiserDonations(donorId, fundraiserId) {
      // const donorId = this.donorAccounts_id;
      try {
        const response = await api.get(
          `/fundraiser-donations?donorAccounts_id=${donorId}&fundraisers_id=${fundraiserId}`,
        );
        this.fundraiserDonations = response.data.data;
      } catch (error) {
        console.error('Failed to fetch fundraiser donations for donor:', error);
        this.fundraiserDonations = [];
      }
    },
    async getFundraiserDonationData(givesDaysId) {
      const db = getDatabase(app);

      const donations = ref(db, `giving_affiliate/${givesDaysId}/campaigns`);

      onValue(donations, (snapshot) => {
        const data = snapshot.val();
        this.fundraiserDonationData = {
          ...this.fundraiserDonationData,
          ...data,
        };
      });
    },
    async bringAllOrganizations() {
      this.isLoading = true;
      try {
        const response = await api.get('/basic-org-data?hostId=' + useDonorStore().donorInfo.hosts_id);
        this.organizations = response.data.data;
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        this.organizations = [];
      } finally {
        this.isLoading = false;
      }
    },
    async publishFundraiser() {
      try {
        // TODO: Throw Error Org not found/Selected
        let requestData = {
          creator_email: useDonorStore().donorInfo.email,
          creator_name: useDonorStore().donorFullName,
          title: this.fundraiserTitle,
          urlLink: this.urlLink,
          goal: this.goal,
          imageBase64: this.image,
          summary: this.story,
          organizations_hosts_id: this.benefittingOrg.organizations_hosts_id,
          donorAccounts_id: useDonorStore().donorInfo.donorAccounts_id,
          hosts_id: useDonorStore().donorInfo.hosts_id,
          companyGiving_id: this.companyGiving?.companyGiving_id,
        };

        this.isLoading = true;

        const response = await api.put('/create-fundraiser', requestData);
        if (response.status === 200) {
          this.createFundraiser = false;
          this.editFundraiser = false;
          this.newlyCreatedFundraiserId = response.data.data.fundraisers_id;
        }
        this.createFundraiser = true;
      } catch (error) {
        console.error('Failed to create fundraiser:', error);
        this.saveError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async switchToFundraiserDetailsPage(type) {
      const fundraiserStore = useFundraiserStore();
      await fundraiserStore.fetchFundraisers();
      await useDonorStore().fetchFundraisers();
      router.push(
        type == 'company'
          ? `/company/fundraisers/${this.newlyCreatedFundraiserId}`
          : `/fundraisers/${this.newlyCreatedFundraiserId}`,
      );
      this.newlyCreatedFundraiserId = '';
    },
    async updateFundraiser() {
      try {
        let requestData = {
          creator_name: useDonorStore().donorFullName,
          title: this.fundraiserTitle,
          urlLink: this.urlLink,
          goal: this.goal,
          imageBase64: this.image || null, // Use new image if available
          summary: this.story,
          donorAccounts_id: useDonorStore().donorInfo.donorAccounts_id,
        };

        // If no new image was uploaded, keep the old one
        if (!this.image) {
          delete requestData.imageBase64;
        }

        this.isLoading = true;

        const response = await api.put(`/update-fundraiser/${this.fundraisers_id}`, requestData);
        if (response.status === 200) {
          this.fundraiserUpdated = true;
        }
      } catch (error) {
        console.error('Failed to update fundraiser:', error);
        this.saveError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async checkFundraiserUrl() {
      try {
        this.setLoading(true);
        await api.post(`/check-fundraiser-urlLink?urlLink=${this.urlLink}`);
      } catch (error) {
        this.errors.urlLink = 'URL already exists.';
      } finally {
        this.setLoading(false);
      }
    },
    //checks if the host allows users to create fundraisers
    async checkFundraiserCreation() {
      if (
        1 == useDonorStore().featureCampaigns &&
        1 == useDonorStore().hostsDynamicParams.giving_features_campaignCreation_on
      ) {
        this.fundraiserCreationAllowed = true;
        return;
      }

      try {
        await useModesStore().fetchModes();

        if (useModesStore().activeMode && useDonorStore().hostsDynamicParams?.giving_features_campaignCreation_modes) {
          const supportedModes = useDonorStore().hostsDynamicParams.giving_features_campaignCreation_modes.split(',');
          if (useModesStore().activeMode.name && supportedModes.includes(useModesStore().activeMode.name)) {
            this.fundraiserCreationAllowed = true;
          }
        }
      } catch (error) {
        console.error('Failed to check fundraiser creation:', error);
      }
    },
    resetData() {
      this.fundraiserTitle = '';
      this.urlLink = '';
      this.goal = null;
      this.story = '';
      this.image = '';
      this.errors = {};
      this.fundraisers_id = null;
      this.createFundraiser = false;
      this.heroLink = '';
      this.editFundraiser = false;
      this.success = false;
    },
  },

  getters: {
    benefittingOrg() {
      return this.organizations.find((org) => org.organizations_hosts_id == this.benefittingOrgHostsId);
    },
    benefittingCompany() {
      return useCompanyAdminStore().companies.find(
        (company) => company.companyGiving_id == this.benefittingCompanyGivingId,
      );
    },
    orgLogoUrl() {
      return (orgId) => {
        return `https://ddb9l06w3jzip.cloudfront.net/uploadedFiles/giving_affiliate/organizations/logo/${orgId}`;
      };
    },
  },
});
